import { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { ArrowRight, Check, Bell, BarChart3, MessageSquare, Users, FileDown, Sparkles, ChevronDown, ChevronLeft, ChevronRight, Clock, Mail, FileText, AlertTriangle, TrendingDown, Zap, Lock, Globe, HeadphonesIcon, Brain, Target, Send, Calendar, Building2, BookOpen } from 'lucide-react';
import { articles } from '@/data/blogArticles';
import Logo from '@/components/Logo';

function AnnotationPin({ x, y, label, side = 'right', color, index }) {
  const GAP = 14; // longueur du petit connecteur entre le point et la bulle
  return (
    <div
      className="absolute"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        opacity: 0,
        animation: 'annotateFade 0.45s ease-out forwards',
        animationDelay: `${0.35 + index * 0.22}s`,
        zIndex: 20,
      }}
    >
      {/* Halo pulsé — situe le point exact sur la capture */}
      <div
        className="absolute rounded-full"
        style={{
          width: 20, height: 20, left: -10, top: -10,
          backgroundColor: color,
          opacity: 0.18,
          animation: 'pulse-ring 2.6s ease-in-out infinite',
          animationDelay: `${index * 0.5}s`,
        }}
      />
      {/* Point de repère précis */}
      <div
        className="absolute rounded-full"
        style={{
          width: 7, height: 7, left: -3.5, top: -3.5,
          backgroundColor: color,
          border: '2px solid white',
          boxShadow: '0 1px 4px rgba(15,26,46,0.35)',
          zIndex: 2,
        }}
      />
      {/* Connecteur fin + bulle — même langage visuel que les cartes du produit (bordure colorée à gauche) */}
      <div
        className="absolute flex items-center"
        style={{
          top: -10,
          ...(side === 'right'
            ? { left: 4, flexDirection: 'row' }
            : { right: 4, flexDirection: 'row-reverse' }
          ),
        }}
      >
        <div style={{ width: GAP, height: 1.5, backgroundColor: color, opacity: 0.55 }} />
        <div
          className="whitespace-nowrap rounded-md bg-white"
          style={{
            padding: '5px 10px',
            fontSize: '10.5px',
            fontWeight: 700,
            color: '#0F1A2E',
            boxShadow: '0 6px 18px rgba(15,26,46,0.16), 0 0 0 1px rgba(15,26,46,0.05)',
            borderLeft: `3px solid ${color}`,
          }}
        >
          {label}
        </div>
      </div>
    </div>
  );
}

const CAROUSEL_SLIDES = [
  {
    img: '/screenshots/dashboard.png?v=4',
    icon: BarChart3,
    badge: 'Dashboard',
    color: '#1B4FD8',
    title: 'Toute votre trésorerie contractuelle, d\'un coup d\'œil.',
    desc: 'Engagement mensuel, alertes actives, économies réalisées — tout ce qui compte, centralisé sans effort.',
    annotations: [
      { x: 31, y: 30, label: '13 contrats centralisés', side: 'right' },
      { x: 27, y: 45, label: '780 € d\'économies tracées', side: 'right' },
      { x: 28, y: 56, label: 'Conseil IA personnalisé', side: 'right' },
    ],
  },
  {
    img: '/screenshots/contracts.png?v=4',
    icon: FileText,
    badge: 'Contrats',
    color: '#00D4AA',
    title: 'Tous vos contrats. Filtrés. Classés. Sous contrôle.',
    desc: 'Par catégorie, montant, fournisseur ou statut — retrouvez n\'importe quel contrat en 3 secondes.',
    annotations: [
      { x: 20, y: 45, label: 'Statut visuel instantané', side: 'right' },
      { x: 87, y: 46, label: 'Préavis calculés auto', side: 'left' },
    ],
  },
  {
    img: '/screenshots/calendar.png?v=4',
    icon: Calendar,
    badge: 'Calendrier',
    color: '#6366F1',
    title: 'Toutes vos échéances, au bon moment.',
    desc: 'Reconductions, rétractations, rappels — visualisez tout le calendrier contractuel sur 12 mois.',
    annotations: [
      { x: 73, y: 49, label: 'Reconductions visualisées', side: 'left' },
      { x: 85, y: 14, label: '4 types d\'alertes', side: 'left' },
    ],
  },
  {
    img: '/screenshots/alerts.png?v=4',
    icon: Bell,
    badge: 'Alertes',
    color: '#FFB347',
    title: 'J-90, J-30, J-7 — vous êtes prévenu. Toujours.',
    desc: 'Alertes automatiques avant chaque renouvellement critique. Zéro reconduction tacite surprise.',
    annotations: [
      { x: 25, y: 25, label: '5 urgences à traiter', side: 'right' },
      { x: 89, y: 37, label: 'Action en 1 clic', side: 'left' },
    ],
  },
  {
    img: '/screenshots/suppliers.png?v=4',
    icon: Building2,
    badge: 'Fournisseurs',
    color: '#0EA5E9',
    title: 'Tous vos fournisseurs. Un seul endroit.',
    desc: 'Coût annuel, nombre de contrats, catégorie — pilotez votre portefeuille fournisseurs sans effort.',
    annotations: [
      { x: 41, y: 23, label: 'Coût annuel auto-calculé', side: 'right' },
      { x: 78, y: 19, label: 'Fournisseurs consolidés', side: 'left' },
    ],
  },
  {
    img: '/screenshots/reports.png?v=4',
    icon: FileDown,
    badge: 'Rapports PDF',
    color: '#00C97A',
    title: 'Votre analyse contractuelle, exportable en un clic.',
    desc: 'Engagements, projections N+1 / N+2, économies réalisées — en PDF professionnel prêt pour la direction.',
    annotations: [
      { x: 35, y: 30, label: 'Répartition par catégorie', side: 'right' },
      { x: 88, y: 14, label: 'Export PDF en 1 clic', side: 'left' },
    ],
  },
];

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [period, setPeriod] = useState('mensuel');
  const [contractsCount, setContractsCount] = useState(30);
  const [engagement, setEngagement] = useState('medium');
  const [hadReconduction, setHadReconduction] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [contractStackIndex, setContractStackIndex] = useState(0);
  const [savingsCounter, setSavingsCounter] = useState(0);
  const [alertPulse, setAlertPulse] = useState(false);
  const [liveNotif, setLiveNotif] = useState(0);
  const [slideIdx, setSlideIdx] = useState(0);
  const [slideImgError, setSlideImgError] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setContractStackIndex((i) => (i + 1) % 4), 2800);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const target = 12000;
    let current = 0;
    const increment = target / 60;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) { current = target; clearInterval(timer); }
      setSavingsCounter(Math.floor(current));
    }, 30);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setAlertPulse(true);
      setTimeout(() => setAlertPulse(false), 1000);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setLiveNotif(n => n + 1), 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft')  setSlideIdx(s => (s - 1 + CAROUSEL_SLIDES.length) % CAROUSEL_SLIDES.length);
      if (e.key === 'ArrowRight') setSlideIdx(s => (s + 1) % CAROUSEL_SLIDES.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const savings = useMemo(() => {
    const base = contractsCount * 80;
    const multiplier = engagement === 'low' ? 1 : engagement === 'medium' ? 1.5 : 2.5;
    const recondFactor = hadReconduction ? 1.3 : 1;
    const mid = base * multiplier * recondFactor;
    // Round to lower 100 for honesty
    return {
      low: Math.floor(mid * 0.7 / 100) * 100,
      high: Math.floor(mid * 1.3 / 100) * 100,
    };
  }, [contractsCount, engagement, hadReconduction]);

  const fmt = (n) => n.toLocaleString('fr-FR');

  const periodColor = period === 'annuel' ? '#1B4FD8' : '#00D4AA';

  const getPlanId = (planBase, p) => p === 'mensuel' ? planBase : `${planBase}_${p}`;

  const handlePlanSelect = async (planBase) => {
    const planId = getPlanId(planBase, period);
    if (user) {
      try {
        const { data } = await api.post('/api/stripe/create-checkout', {
          plan_id: planId,
          origin_url: window.location.origin,
        });
        window.location.href = data.url;
      } catch {
        window.location.href = '/app';
      }
    } else {
      window.location.href = `/signup?plan=${planBase}&period=${period}`;
    }
  };

  const plans = [
    { id: 'gratuit', name: 'Gratuit', monthly: 0, features: ['3 contrats', 'Extraction IA', 'Alertes email', 'Dashboard', '1 utilisateur'] },
    { id: 'solo', name: 'Solo', monthly: 19, features: ['15 contrats', 'Extraction IA', '6 négociations IA/mois', 'Alertes email', 'Tableau des économies', '2 utilisateurs'] },
    { id: 'pme', name: 'PME', monthly: 49, popular: true, features: ['50 contrats', 'Extraction IA', '15 négociations IA/mois', 'Alertes email', 'Export PDF & rapports', 'Conseiller IA', '4 utilisateurs'] },
    { id: 'power', name: 'Entreprise', monthly: 99, features: ['Contrats illimités', '30 négociations IA/mois', 'Alertes email', 'Export PDF & rapports', '100 messages conseiller IA/mois', 'Multi-sociétés', 'Équipe illimitée'] },
  ];

  // Pricing rounded to LOWER unit
  const getPrice = (monthly) => {
    if (monthly === 0) return { display: '0€', period: '', equiv: null };
    if (period === 'mensuel') return { display: `${monthly}€`, period: '/mois', equiv: null };
    if (period === 'trimestriel') {
      const q = Math.floor(monthly * 3 * 0.95);
      const e = Math.floor(monthly * 0.95);
      return { display: `${q}€`, period: '/trim.', equiv: `soit ${e}€/mois` };
    }
    const y = Math.floor(monthly * 12 * 0.9);
    const e = Math.floor(monthly * 0.9);
    return { display: `${y}€`, period: '/an', equiv: `soit ${e}€/mois` };
  };

  const faqs = [
    {
      q: 'Comment mes données sont-elles protégées ?',
      a: 'La sécurité de vos contrats est notre priorité absolue. Toutes vos données sont hébergées sur des serveurs européens chiffrés AES-256, conformes au RGPD. Chaque connexion est sécurisée par un protocole HTTPS avec certificat SSL. Des sauvegardes automatiques quotidiennes sont effectuées pour garantir qu\'aucune donnée ne soit perdue.'
    },
    {
      q: 'Puis-je annuler mon abonnement facilement ?',
      a: 'Les abonnements MyContrat s\'entendent pour la durée choisie lors de la souscription. Un abonnement mensuel est valable un mois, un abonnement trimestriel est valable trois mois, un abonnement annuel est valable un an. Aucun remboursement partiel n\'est effectué en cas de résiliation anticipée. Vous pouvez à tout moment décider de ne pas renouveler votre abonnement à son échéance, sans frais supplémentaires. Pour toute demande, contactez-nous par email ou par téléphone.'
    },
    {
      q: 'Mon équipe aura-t-elle besoin d\'une formation ?',
      a: 'Aucune formation requise. MyContrat a été pensé pour les dirigeants et les équipes administratives qui n\'ont pas le temps d\'apprendre un nouvel outil. L\'interface est intuitive et la prise en main se fait en moins de 10 minutes, même pour les profils les moins à l\'aise avec le numérique. Des info-bulles contextuelles vous accompagnent dans chaque action, et notre équipe support est disponible à tout moment si vous avez une question.'
    },
    {
      q: 'Comment importer mes contrats existants ?',
      a: 'C\'est l\'une des fonctionnalités les plus appréciées. Il vous suffit de glisser-déposer vos PDF ou documents Word dans l\'interface. Notre intelligence artificielle analyse chaque document en quelques secondes et extrait automatiquement toutes les informations clés : dates d\'échéance, montants, conditions de résiliation, clauses de reconduction tacite. Vous validez en un clic. Pour les entreprises ayant de nombreux contrats, notre équipe peut également réaliser l\'import complet pour vous dans le cadre de notre accompagnement personnalisé.'
    },
    {
      q: 'Que se passe-t-il si l\'IA commet une erreur sur un contrat ?',
      a: 'L\'intelligence artificielle propose, vous validez. Chaque extraction est systématiquement présentée pour vérification avant enregistrement. Vous voyez le contrat original à côté des données extraites et pouvez corriger n\'importe quel champ en un clic. Si vous détectez une erreur après enregistrement, vous pouvez modifier les informations à tout moment depuis la fiche du contrat. Nous considérons chaque retour comme une opportunité d\'améliorer l\'IA pour la rendre toujours plus précise.'
    },
    {
      q: 'Quels sont les formats de documents acceptés ?',
      a: 'MyContrat accepte les contrats au format PDF ainsi que les documents Google Docs. Pour les fichiers scannés, notre technologie d\'OCR (reconnaissance optique de caractères) permet également de les analyser correctement, à condition que la qualité du scan soit suffisante. Si vous avez un format particulier à importer, contactez-nous, nous étudions toutes les demandes.'
    },
    {
      q: 'Proposez-vous un accompagnement personnalisé ?',
      a: 'Absolument. Nous accompagnons toutes les entreprises qui le souhaitent, quelle que soit leur taille. Notre service client est disponible du lundi au samedi, de 8h30 à 19h, par email. Pour les PME souhaitant un onboarding complet, nous proposons un service où nous récupérons vos contrats existants, les importons à votre place, configurons vos alertes et formons votre équipe. Chaque ticket est traité dans un délai maximum de 24h ouvrées, et la plupart des demandes reçoivent une réponse dans l\'heure.'
    },
    {
      q: 'Quelle est la fiabilité de MyContrat ?',
      a: 'MyContrat bénéficie d\'une infrastructure cloud de niveau entreprise avec un taux de disponibilité de 99,9%. Nos serveurs sont hébergés dans des datacenters certifiés ISO 27001 situés en France et en Europe. Toutes les opérations sont monitorées 24h/24 et 7j/7. En cas de maintenance programmée, vous êtes prévenu au moins 48h à l\'avance et les interventions sont planifiées en dehors des heures de bureau pour ne jamais perturber votre activité.'
    },
  ];

  const floatingContracts = [
    { name: 'Orange Business', amount: '340€/mois', days: 3, color: '#FF4D6A', category: 'Téléphonie' },
    { name: 'AXA Multirisque', amount: '1 200€/an', days: 42, color: '#FFB347', category: 'Assurance' },
    { name: 'ClimaSud HVAC', amount: '450€/an', days: 19, color: '#FFB347', category: 'Maintenance' },
    { name: 'Salesforce Pro', amount: '89€/mois', days: 71, color: '#00C97A', category: 'Logiciels' },
  ];

  const liveNotifs = [
    { text: 'Alerte J-7 envoyée', time: 'il y a 2 min' },
    { text: 'Contrat Salesforce analysé', time: 'il y a 5 min' },
    { text: 'Email de résiliation généré', time: 'il y a 8 min' },
    { text: 'Rapport mensuel exporté', time: 'il y a 12 min' },
  ];

  const currentSlide = CAROUSEL_SLIDES[slideIdx];
  const SlideIcon = currentSlide.icon;

  return (
    <>
      <Helmet>
        <title>MyContrat — Logiciel de gestion de contrats pour PME françaises</title>
        <meta name="description" content="Centralisez vos contrats, recevez des alertes avant chaque échéance et économisez en moyenne 12 000€ par an. Extraction IA automatique. Essai gratuit sans carte bancaire." />
        <link rel="canonical" href="https://www.mycontrat.fr/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.mycontrat.fr/" />
        <meta property="og:title" content="MyContrat — Gestion de contrats pour PME françaises" />
        <meta property="og:description" content="Zéro saisie. Zéro oubli. Zéro surprise. L'outil qui évite les reconductions tacites non souhaitées." />
        <meta property="og:image" content="https://www.mycontrat.fr/og-image.png" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="MyContrat" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="MyContrat — Gestion de contrats pour PME françaises" />
        <meta name="twitter:description" content="Centralisez vos contrats, recevez des alertes avant chaque échéance, économisez 12 000€/an." />
        <meta name="twitter:image" content="https://www.mycontrat.fr/og-image.png" />
      </Helmet>

      {/* FAQPage JSON-LD : reflète les questions réellement affichées plus bas sur cette page (section #faq) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          }),
        }}
      />

      <div className="min-h-screen bg-white text-[#0F1A2E] overflow-x-hidden" style={{ fontFeatureSettings: "'tnum'" }}>
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/80 backdrop-blur-xl border-b border-black/[0.06]' : 'bg-transparent'}`} aria-label="Navigation principale">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <Logo size={36} showText={true} linkTo="/" />
            <div className="hidden md:flex items-center gap-8 text-sm text-[#6B7A99]">
              <a href="#produit" className="hover:text-[#0F1A2E] transition-colors">Produit</a>
              <a href="#calculateur" className="hover:text-[#0F1A2E] transition-colors">Calculateur</a>
              <a href="#tarifs" className="hover:text-[#0F1A2E] transition-colors">Tarifs</a>
              <a href="#blog" className="hover:text-[#0F1A2E] transition-colors">Blog</a>
              <a href="#faq" className="hover:text-[#0F1A2E] transition-colors">FAQ</a>
              <a href="#contact" onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }} className="hover:text-[#0F1A2E] transition-colors cursor-pointer">Contact</a>
            </div>
            <div className="flex items-center gap-3">
              {user ? (
                <button onClick={() => navigate('/app')} className="bg-[#0F1A2E] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#1B4FD8] transition-all">
                  Mon espace
                </button>
              ) : (
                <>
                  <a href="/login" className="text-sm text-[#6B7A99] hover:text-[#0F1A2E]">Connexion</a>
                  <a href="/signup" className="relative bg-gradient-to-br from-[#00D4AA] to-[#00B894] text-[#0F1A2E] text-sm font-bold px-4 py-2 rounded-lg hover:shadow-lg hover:shadow-[#00D4AA]/40 transition-all overflow-hidden group">
                    <span className="relative z-10">Essai gratuit</span>
                    <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                  </a>
                </>
              )}
            </div>
          </div>
        </nav>

        {/* HERO */}
        <section className="relative pt-32 pb-24 px-6 overflow-hidden" aria-labelledby="hero-title">
          <div className="absolute top-20 left-0 w-[500px] h-[500px] bg-[#00D4AA]/15 rounded-full blur-[120px] pointer-events-none"></div>
          <div className="absolute top-40 right-0 w-[600px] h-[600px] bg-[#1B4FD8]/10 rounded-full blur-[140px] pointer-events-none"></div>

          <div className="relative max-w-7xl mx-auto grid lg:grid-cols-[1.1fr_1fr] gap-16 items-center">
            <div>
              {/* Live notification ticker - the discrete element that represents the tool */}
              <div className="relative inline-flex items-center gap-3 bg-white border border-black/[0.08] px-4 py-2.5 rounded-full mb-8 shadow-sm overflow-hidden">
                <div className="relative flex w-2 h-2 flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00D4AA] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00D4AA]"></span>
                </div>
                <div className="relative h-4 overflow-hidden w-[220px]">
                  {liveNotifs.map((n, i) => (
                    <div
                      key={i}
                      className="absolute inset-0 flex items-center gap-2 text-xs transition-all duration-700"
                      style={{
                        transform: `translateY(${(i - (liveNotif % liveNotifs.length)) * 100}%)`,
                        opacity: i === (liveNotif % liveNotifs.length) ? 1 : 0,
                      }}
                    >
                      <span className="text-[#0F1A2E] font-semibold whitespace-nowrap">{n.text}</span>
                      <span className="text-[#6B7A99] text-[10px] whitespace-nowrap">· {n.time}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#1B4FD8]/10 to-[#00D4AA]/10 border border-[#1B4FD8]/20 rounded-full px-4 py-1.5 mb-4">
                <div className="w-2 h-2 rounded-full bg-[#00D4AA] animate-pulse" />
                <span className="text-sm font-semibold bg-gradient-to-r from-[#1B4FD8] to-[#00D4AA] bg-clip-text text-transparent">
                  Boosté à l'IA
                </span>
                <Sparkles className="w-3.5 h-3.5 text-[#00D4AA]" />
              </div>

              <h1 id="hero-title" className="text-4xl md:text-5xl font-black text-[#0F1A2E] leading-tight mb-4">
                Le logiciel de gestion de contrats fournisseur à destination des PME
              </h1>

              <h2 className="text-xl md:text-2xl font-semibold text-[#0F1A2E] leading-snug mb-4">
                Combien la mauvaise gestion de vos contrats fournisseurs vous coûte-t-elle <span className="text-[#00D4AA]">vraiment</span> ?
              </h2>

              <p className="text-lg text-[#6B7A99] mt-4 max-w-xl leading-relaxed mb-8">
                MyContrat vous alerte avant chaque échéance et vous redonne le contrôle.
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                <a href="/signup" className="relative bg-gradient-to-br from-[#00D4AA] to-[#00B894] text-[#0F1A2E] font-bold px-7 py-4 rounded-xl hover:shadow-xl hover:shadow-[#00D4AA]/40 transition-all hover:-translate-y-0.5 flex items-center gap-2 group overflow-hidden">
                  <span className="relative z-10 flex items-center gap-2">
                    Essai gratuit — 3 contrats offerts
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                  </span>
                  <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                </a>
                <a href="#demo" onClick={(e) => { e.preventDefault(); document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' }); }} className="border-2 border-[#0F1A2E]/10 px-7 py-4 rounded-xl hover:border-[#0F1A2E] hover:bg-[#0F1A2E] hover:text-white transition-all font-bold">
                  Voir la démo
                </a>
              </div>

              <div className="flex flex-wrap gap-6 text-xs text-[#6B7A99] mt-6">
                <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#00D4AA]" aria-hidden="true" /> Sans carte bancaire</span>
                <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#00D4AA]" aria-hidden="true" /> Conforme RGPD</span>
                <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#00D4AA]" aria-hidden="true" /> Hébergement France</span>
              </div>
            </div>

            <div className="relative h-[520px] flex items-center justify-center">
              <div className="absolute top-4 left-4 bg-white border border-black/[0.08] rounded-2xl p-4 shadow-xl shadow-[#0F1A2E]/5 z-30" style={{ animation: 'float 4s ease-in-out infinite' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#00C97A]/20 to-[#00D4AA]/20 rounded-xl flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-[#00C97A]" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-[10px] text-[#6B7A99] uppercase font-bold tracking-wider">Économies</div>
                    <div className="text-xl font-black tabular-nums">+3 240 €</div>
                  </div>
                </div>
              </div>

              <div className="absolute bottom-20 right-4 bg-white border border-black/[0.08] rounded-2xl p-4 shadow-xl shadow-[#0F1A2E]/5 z-30" style={{ animation: 'float 5s ease-in-out infinite 0.5s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#1B4FD8]/20 to-[#1B4FD8]/10 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-[#1B4FD8]" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-[10px] text-[#6B7A99] uppercase font-bold tracking-wider">Contrats</div>
                    <div className="text-xl font-black tabular-nums">32 actifs</div>
                  </div>
                </div>
              </div>

              <div className="absolute bottom-16 left-0 bg-white border border-black/[0.06] rounded-2xl p-3 shadow-lg shadow-[#0F1A2E]/5 z-30" style={{ animation: 'float 6s ease-in-out infinite 1s' }}>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 bg-[#00C97A]/15 rounded-xl flex items-center justify-center flex-shrink-0">
                    <TrendingDown className="w-4 h-4 text-[#00C97A]" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-[10px] text-[#6B7A99] uppercase font-bold tracking-wider">Ce mois</div>
                    <div className="text-sm font-black">2 reconductions évitées</div>
                  </div>
                </div>
              </div>

              <div className={`absolute top-32 right-0 bg-gradient-to-br from-[#FF4D6A] to-[#FF4D6A]/90 text-white rounded-2xl p-4 shadow-2xl shadow-[#FF4D6A]/30 z-40 transition-all duration-500 ${alertPulse ? 'scale-110' : 'scale-100'}`}>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="absolute inset-0 bg-white/40 rounded-full animate-ping"></div>
                    <AlertTriangle className="relative w-5 h-5" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-[10px] opacity-80 uppercase font-bold tracking-wider">Alerte</div>
                    <div className="text-sm font-bold">J-3 · Orange Pro</div>
                  </div>
                </div>
              </div>

              <div className="relative w-[360px] h-[420px]">
                {floatingContracts.map((contract, i) => {
                  const position = (i - contractStackIndex + 4) % 4;
                  return (
                    <div
                      key={i}
                      className="absolute left-0 right-0 bg-white border border-black/[0.08] rounded-2xl p-5 shadow-xl shadow-[#0F1A2E]/5 transition-all duration-700"
                      style={{
                        top: `${position * 14}px`,
                        transform: `scale(${1 - position * 0.04}) translateY(${position * -6}px)`,
                        zIndex: 20 - position,
                        opacity: position === 3 ? 0 : 1 - position * 0.15,
                        transitionTimingFunction: 'cubic-bezier(0.23,1,0.32,1)',
                      }}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="text-[10px] text-[#6B7A99] uppercase font-bold tracking-wider mb-1">{contract.category}</div>
                          <div className="font-black text-lg tracking-tight">{contract.name}</div>
                        </div>
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold tabular-nums" style={{ background: `${contract.color}15`, color: contract.color }}>
                          <Clock className="w-3 h-3" aria-hidden="true" />
                          J-{contract.days}
                        </div>
                      </div>

                      <div className="flex items-end justify-between">
                        <div>
                          <div className="text-[10px] text-[#6B7A99] uppercase font-bold tracking-wider">Montant</div>
                          <div className="text-2xl font-black tabular-nums">{contract.amount}</div>
                        </div>
                        <div className="flex gap-1">
                          <div className="w-8 h-8 rounded-lg bg-[#F7F9FC] flex items-center justify-center">
                            <Bell className="w-3.5 h-3.5 text-[#6B7A99]" aria-hidden="true" />
                          </div>
                          <div className="w-8 h-8 rounded-lg bg-[#F7F9FC] flex items-center justify-center">
                            <MessageSquare className="w-3.5 h-3.5 text-[#6B7A99]" aria-hidden="true" />
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-black/[0.06]">
                        <div className="flex justify-between text-[10px] text-[#6B7A99] mb-1.5">
                          <span className="font-bold">Temps avant échéance</span>
                          <span className="tabular-nums">{Math.round((contract.days / 90) * 100)}%</span>
                        </div>
                        <div className="h-1.5 bg-[#F7F9FC] rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${(contract.days / 90) * 100}%`, background: contract.color }}></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <style>{`@keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }`}</style>
        </section>

        {/* STATS BAR */}
        <section className="relative py-10 bg-[#0F1A2E] text-white overflow-hidden" aria-label="Chiffres clés">
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1A2E] via-[#1B4FD8]/20 to-[#0F1A2E]"></div>
          <div className="relative max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-5 gap-8">
            {[
              { value: '8', label: 'Types de contrats reconnus' },
              { value: 'J-90/J-30/J-7', label: 'Alertes avant chaque échéance' },
              { value: '< 10 s', label: 'Extraction IA par contrat' },
              { value: '10 min', label: 'Mise en place totale' },
              { value: '< 24h', label: 'Délai de réponse support' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-black tracking-tight mb-1 bg-gradient-to-br from-white to-[#00D4AA] bg-clip-text text-transparent tabular-nums">
                  {stat.value}
                </div>
                <div className="text-xs text-white/60 uppercase font-bold tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ROI CALCULATOR */}
        <section id="calculateur" className="relative py-32 px-6" aria-labelledby="calc-title">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block text-xs font-black text-[#00D4AA] uppercase tracking-[0.3em] mb-4">Calculateur</div>
              <h2 id="calc-title" className="text-4xl lg:text-6xl font-black tracking-tight mb-4">
                Combien pourriez-vous<br />
                <span className="bg-gradient-to-r from-[#1B4FD8] to-[#00D4AA] bg-clip-text text-transparent">économiser ?</span>
              </h2>
              <p className="text-lg text-[#6B7A99]">Trois questions. Résultat immédiat.</p>
            </div>

            <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
              <div className="bg-white border border-black/[0.08] rounded-3xl p-8 lg:p-10 shadow-xl shadow-[#0F1A2E]/5">
                <div className="mb-10">
                  <div className="flex items-center justify-between mb-4">
                    <label htmlFor="contracts-range" className="text-sm font-bold">Combien de contrats gérez-vous ?</label>
                    <div className="flex items-center gap-2 bg-gradient-to-br from-[#0F1A2E] to-[#1B4FD8] text-white px-4 py-2 rounded-xl">
                      <span className="text-3xl font-black tracking-tight tabular-nums">{contractsCount}</span>
                    </div>
                  </div>
                  <input id="contracts-range" type="range" min="5" max="100" value={contractsCount} onChange={(e) => setContractsCount(Number(e.target.value))}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer"
                    style={{ background: `linear-gradient(to right, #00D4AA 0%, #1B4FD8 ${((contractsCount - 5) / 95) * 100}%, #F7F9FC ${((contractsCount - 5) / 95) * 100}%, #F7F9FC 100%)` }} />
                  <div className="flex justify-between text-[10px] text-[#6B7A99] mt-2 font-bold">
                    <span>5</span><span>50</span><span>100+</span>
                  </div>
                </div>

                <div className="mb-10">
                  <label className="text-sm font-bold block mb-4">Votre engagement mensuel total ?</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[{ id: 'low', label: '< 500€' }, { id: 'medium', label: '500 — 2k€' }, { id: 'high', label: '> 2 000€' }].map(opt => (
                      <button key={opt.id} onClick={() => setEngagement(opt.id)} type="button"
                        className={`py-3.5 px-4 rounded-xl text-sm font-bold transition-all ${engagement === opt.id ? 'bg-gradient-to-br from-[#0F1A2E] to-[#1B4FD8] text-white shadow-lg shadow-[#1B4FD8]/30' : 'bg-[#F7F9FC] text-[#6B7A99] hover:bg-[#F7F9FC]/70'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-bold block mb-4">Une reconduction non souhaitée vous est déjà arrivée ?</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[{ val: true, label: 'Oui' }, { val: false, label: 'Non' }].map(opt => (
                      <button key={String(opt.val)} onClick={() => setHadReconduction(opt.val)} type="button"
                        className={`py-3.5 px-4 rounded-xl text-sm font-bold transition-all ${hadReconduction === opt.val ? 'bg-gradient-to-br from-[#0F1A2E] to-[#1B4FD8] text-white shadow-lg shadow-[#1B4FD8]/30' : 'bg-[#F7F9FC] text-[#6B7A99] hover:bg-[#F7F9FC]/70'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="relative bg-gradient-to-br from-[#0F1A2E] via-[#1B4FD8] to-[#0F1A2E] text-white rounded-3xl p-8 lg:p-10 overflow-hidden flex flex-col justify-between">
                <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#00D4AA]/20 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#FF4D6A]/10 rounded-full blur-3xl"></div>

                <div className="relative">
                  <div className="text-xs text-[#00D4AA] uppercase tracking-[0.3em] font-black mb-4">Vos économies estimées</div>
                  <div className="text-5xl lg:text-6xl font-black tracking-tighter leading-none mb-3 tabular-nums">
                    {fmt(savings.low)}€
                  </div>
                  <div className="text-3xl font-bold text-white/40 mb-1">à {fmt(savings.high)} €</div>
                  <div className="text-sm text-white/60 mb-8">par an avec MyContrat</div>

                  <div className="space-y-3 mb-8">
                    {['Reconductions tacites évitées', 'Renégociations facilitées', 'Doublons identifiés'].map((txt, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <div className="w-6 h-6 rounded-full bg-[#00D4AA]/20 flex items-center justify-center">
                          <Check className="w-3.5 h-3.5 text-[#00D4AA]" aria-hidden="true" />
                        </div>
                        <span className="text-white/90">{txt}</span>
                      </div>
                    ))}
                  </div>

                  <a href="/signup" className="block text-center bg-[#00D4AA] text-[#0F1A2E] font-black px-6 py-4 rounded-xl hover:shadow-xl hover:shadow-[#00D4AA]/30 transition-all">
                    Commencer gratuitement →
                  </a>
                </div>
                <div className="relative text-[10px] text-white/40 mt-6 leading-relaxed">
                  Estimation à partir des données publiées par la CPME et l'Observatoire des PME françaises (2024).
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="produit" className="py-32 px-6 bg-gradient-to-b from-white via-[#F7F9FC]/50 to-white" aria-labelledby="how-title">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-20">
              <div className="inline-block text-xs font-black text-[#1B4FD8] uppercase tracking-[0.3em] mb-4">Comment ça marche</div>
              <h2 id="how-title" className="text-4xl lg:text-6xl font-black tracking-tight">
                Trois étapes.
              </h2>
            </div>

            <div className="space-y-8">
              {[
                { n: '01', title: 'Importez', desc: 'Glissez un PDF. L\'IA extrait les dates, montants et clauses en moins de 10 secondes.', accent: '#00D4AA' },
                { n: '02', title: 'Centralisez', desc: 'Tous vos contrats dans un dashboard unique. Triez, filtrez, comparez.', accent: '#1B4FD8' },
                { n: '03', title: 'Soyez alerté', desc: 'Alertes automatiques à J-90, J-30 et J-7 avant chaque échéance. Décidez sereinement, sans mauvaise surprise.', accent: '#FF4D6A' },
              ].map((step, i) => (
                <div key={i} className="group grid md:grid-cols-[1fr_1.2fr] gap-8 items-center bg-white border border-black/[0.06] rounded-3xl p-8 lg:p-10 hover:shadow-2xl hover:shadow-[#0F1A2E]/5 transition-all hover:-translate-y-1">
                  <div>
                    <div className="text-[120px] font-black leading-none tracking-tighter mb-4" style={{
                      background: `linear-gradient(135deg, ${step.accent}, ${step.accent}40)`,
                      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                    }}>{step.n}</div>
                    <h3 className="text-3xl font-black mb-3 tracking-tight">{step.title}</h3>
                    <p className="text-[#6B7A99] leading-relaxed text-lg">{step.desc}</p>
                  </div>

                  <div className="relative">
                    {i === 0 && (
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#00D4AA]/10 to-transparent rounded-2xl blur-xl"></div>
                        <div className="relative bg-white border-2 border-dashed border-[#00D4AA]/40 rounded-2xl p-10 text-center">
                          <div className="relative inline-block mb-4">
                            <div className="absolute inset-0 bg-[#00D4AA]/20 rounded-2xl blur-lg animate-pulse"></div>
                            <div className="relative w-16 h-16 bg-gradient-to-br from-[#00D4AA] to-[#00B894] rounded-2xl flex items-center justify-center">
                              <FileDown className="w-8 h-8 text-white" aria-hidden="true" />
                            </div>
                          </div>
                          <div className="text-sm font-black mb-1">contrat-orange-2026.pdf</div>
                          <div className="text-xs text-[#6B7A99] mb-4">1,2 Mo</div>
                          <div className="h-2 bg-[#F7F9FC] rounded-full overflow-hidden relative">
                            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#00D4AA] to-[#1B4FD8] rounded-full" style={{ width: '60%', animation: 'progress 2s ease-in-out infinite' }}></div>
                          </div>
                          <div className="text-[10px] text-[#00D4AA] font-bold mt-2 flex items-center justify-center gap-1">
                            <Sparkles className="w-3 h-3" aria-hidden="true" />
                            Analyse IA en cours...
                          </div>
                        </div>
                      </div>
                    )}
                    {i === 1 && (
                      <div className="space-y-2">
                        {[
                          { name: 'Orange Business', cat: 'Téléphonie', amount: '340€/mois', color: '#FF4D6A' },
                          { name: 'AXA Multirisque', cat: 'Assurance', amount: '1 200€/an', color: '#FFB347' },
                          { name: 'ClimaSud', cat: 'Maintenance', amount: '450€/an', color: '#00C97A' },
                          { name: 'Salesforce', cat: 'Logiciels', amount: '89€/mois', color: '#1B4FD8' },
                        ].map((c, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-3 bg-white border border-black/[0.06] rounded-xl hover:border-[#1B4FD8]/30 transition-colors">
                            <div className="w-1 h-8 rounded-full" style={{ background: c.color }}></div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-black truncate">{c.name}</div>
                              <div className="text-[10px] text-[#6B7A99]">{c.cat}</div>
                            </div>
                            <div className="text-xs font-bold tabular-nums">{c.amount}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {i === 2 && (
                      <div className="space-y-3">
                        {[
                          { label: 'J-90', title: 'Salesforce Pro', desc: 'Renouvellement dans 3 mois', color: '#00C97A', icon: Mail },
                          { label: 'J-30', title: 'AXA Multirisque', desc: 'Attention, décision à prendre', color: '#FFB347', icon: Bell },
                          { label: 'J-7', title: 'Orange Business', desc: 'Urgent — préavis demain', color: '#FF4D6A', icon: AlertTriangle },
                        ].map((alert, idx) => {
                          const Icon = alert.icon;
                          return (
                            <div key={idx} className="flex items-start gap-3 p-3 rounded-xl border" style={{ background: `${alert.color}08`, borderColor: `${alert.color}30` }}>
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${alert.color}15`, color: alert.color }}>
                                <Icon className="w-5 h-5" aria-hidden="true" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="text-[10px] font-black px-1.5 py-0.5 rounded" style={{ background: alert.color, color: 'white' }}>{alert.label}</span>
                                  <span className="text-xs font-black">{alert.title}</span>
                                </div>
                                <div className="text-[10px] text-[#6B7A99]">{alert.desc}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <style>{`@keyframes progress { 0%, 100% { width: 40%; } 50% { width: 85%; } }`}</style>
        </section>

        {/* PRODUCT CAROUSEL */}
        <section id="demo" className="py-24 px-6 bg-[#F7F9FC]" aria-labelledby="demo-title">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block text-xs font-black text-[#1B4FD8] uppercase tracking-[0.3em] mb-4">Le produit</div>
              <h2 id="demo-title" className="text-4xl lg:text-6xl font-black tracking-tight">
                MyContrat <span className="bg-gradient-to-r from-[#1B4FD8] to-[#00D4AA] bg-clip-text text-transparent">en action.</span>
              </h2>
              <p className="text-[#6B7A99] mt-3 text-sm lg:text-base">Le parcours complet, étape par étape — cliquez sur une étape ou une flèche pour explorer.</p>
            </div>

            {/* Parcours utilisateur — stepper (desktop) */}
            <div className="hidden md:flex items-center justify-center flex-wrap gap-1 mb-10" role="tablist" aria-label="Parcours produit">
              {CAROUSEL_SLIDES.map((s, i) => {
                const StepIcon = s.icon;
                const active = i === slideIdx;
                return (
                  <div key={i} className="flex items-center">
                    <button
                      role="tab"
                      aria-selected={active}
                      onClick={() => { setSlideIdx(i); setSlideImgError(false); }}
                      className="flex items-center gap-2 px-3 py-2 rounded-full transition-all duration-300 whitespace-nowrap"
                      style={{
                        backgroundColor: active ? `${s.color}14` : 'transparent',
                        color: active ? s.color : '#6B7A99',
                        fontWeight: active ? 900 : 700,
                      }}
                    >
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black shrink-0 transition-colors duration-300"
                        style={{ backgroundColor: active ? s.color : '#E7ECF3', color: active ? 'white' : '#6B7A99' }}
                      >
                        {i + 1}
                      </span>
                      <StepIcon className="w-3.5 h-3.5 shrink-0" />
                      <span className="text-xs">{s.badge}</span>
                    </button>
                    {i < CAROUSEL_SLIDES.length - 1 && (
                      <ChevronRight className="w-3.5 h-3.5 text-[#6B7A99]/30 mx-0.5 shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Browser frame + arrows */}
            <div className="relative group">
              {/* Arrow left — desktop only, visible on hover */}
              <button
                onClick={() => { setSlideIdx(s => (s - 1 + CAROUSEL_SLIDES.length) % CAROUSEL_SLIDES.length); setSlideImgError(false); }}
                className="hidden lg:flex absolute left-0 top-[42%] -translate-y-1/2 -translate-x-6 z-10 w-11 h-11 bg-white border border-black/[0.08] rounded-full shadow-lg items-center justify-center text-[#6B7A99] hover:bg-[#0F1A2E] hover:text-white hover:border-[#0F1A2E] transition-all duration-200 opacity-0 group-hover:opacity-100 focus:opacity-100"
                aria-label="Fonctionnalité précédente"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Browser chrome frame */}
              <div className="bg-white border border-black/[0.08] rounded-2xl overflow-hidden shadow-2xl shadow-[#0F1A2E]/5">
                <div className="flex items-center gap-1.5 px-4 py-3 border-b border-black/[0.06] bg-[#F7F9FC]">
                  <div className="w-3 h-3 rounded-full bg-[#FF4D6A]/40" />
                  <div className="w-3 h-3 rounded-full bg-[#FFB347]/50" />
                  <div className="w-3 h-3 rounded-full bg-[#00C97A]/50" />
                  <div className="flex-1 text-center text-xs text-[#6B7A99] font-mono">app.mycontrat.fr</div>
                  <div className="text-[10px] font-black text-[#6B7A99]/40 tracking-widest tabular-nums">
                    {String(slideIdx + 1).padStart(2, '0')} / {String(CAROUSEL_SLIDES.length).padStart(2, '0')}
                  </div>
                </div>

                {/* Slide */}
                <div className="relative aspect-[16/10] bg-[#F7F9FC] overflow-hidden">
                  {slideImgError ? (
                    <div
                      key={`ph-${slideIdx}`}
                      className="w-full h-full flex flex-col items-center justify-center animate-fade-in"
                      style={{ background: `linear-gradient(135deg, ${currentSlide.color}06, ${currentSlide.color}14)` }}
                    >
                      <SlideIcon className="w-20 h-20 mb-4" style={{ color: currentSlide.color, opacity: 0.12 }} />
                      <span className="text-sm font-bold" style={{ color: currentSlide.color }}>{currentSlide.badge}</span>
                      <span className="text-xs text-[#6B7A99] mt-1">Screenshot à venir</span>
                    </div>
                  ) : (
                    <>
                      <picture key={`pic-${slideIdx}`}>
                        <source srcSet={currentSlide.img.replace('.png', '.avif')} type="image/avif" />
                        <img
                          key={`img-${slideIdx}`}
                          src={currentSlide.img}
                          alt={currentSlide.title}
                          className="w-full h-full object-cover object-top animate-fade-in"
                          onError={() => setSlideImgError(true)}
                        />
                      </picture>
                      {currentSlide.annotations?.length > 0 && (
                        <div key={`ann-${slideIdx}`} className="absolute inset-0 pointer-events-none">
                          {currentSlide.annotations.map((ann, i) => (
                            <AnnotationPin key={i} {...ann} color={currentSlide.color} index={i} />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Arrow right — desktop only, visible on hover */}
              <button
                onClick={() => { setSlideIdx(s => (s + 1) % CAROUSEL_SLIDES.length); setSlideImgError(false); }}
                className="hidden lg:flex absolute right-0 top-[42%] -translate-y-1/2 translate-x-6 z-10 w-11 h-11 bg-white border border-black/[0.08] rounded-full shadow-lg items-center justify-center text-[#6B7A99] hover:bg-[#0F1A2E] hover:text-white hover:border-[#0F1A2E] transition-all duration-200 opacity-0 group-hover:opacity-100 focus:opacity-100"
                aria-label="Fonctionnalité suivante"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Feature label — animé à chaque changement de slide */}
            <div key={`label-${slideIdx}`} className="mt-8 flex flex-col items-center gap-3 animate-fade-in">
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black"
                style={{ backgroundColor: `${currentSlide.color}18`, color: currentSlide.color }}
              >
                <SlideIcon className="w-3 h-3" />
                {currentSlide.badge}
              </div>
              <h3
                className="text-2xl lg:text-3xl font-black text-center leading-tight px-4"
                style={{
                  color: '#0F1A2E',
                  background: `linear-gradient(90deg, #0F1A2E 30%, ${currentSlide.color})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {currentSlide.title}
              </h3>
              <p className="text-[#6B7A99] max-w-lg text-center leading-relaxed text-sm lg:text-base px-4">
                {currentSlide.desc}
              </p>
            </div>

            {/* Dots + flèches mobile */}
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                onClick={() => { setSlideIdx(s => (s - 1 + CAROUSEL_SLIDES.length) % CAROUSEL_SLIDES.length); setSlideImgError(false); }}
                className="lg:hidden w-9 h-9 bg-white border border-black/[0.08] rounded-full shadow flex items-center justify-center text-[#6B7A99] hover:bg-[#0F1A2E] hover:text-white transition-all"
                aria-label="Précédent"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <div className="flex items-center gap-2" role="tablist" aria-label="Fonctionnalités du produit">
                {CAROUSEL_SLIDES.map((s, i) => (
                  <button
                    key={i}
                    role="tab"
                    aria-selected={i === slideIdx}
                    aria-label={s.badge}
                    onClick={() => { setSlideIdx(i); setSlideImgError(false); }}
                    className="h-2 rounded-full transition-all duration-300"
                    style={{
                      width: i === slideIdx ? '28px' : '8px',
                      backgroundColor: i === slideIdx ? currentSlide.color : '#6B7A99',
                      opacity: i === slideIdx ? 1 : 0.25,
                    }}
                  />
                ))}
              </div>

              <button
                onClick={() => { setSlideIdx(s => (s + 1) % CAROUSEL_SLIDES.length); setSlideImgError(false); }}
                className="lg:hidden w-9 h-9 bg-white border border-black/[0.08] rounded-full shadow flex items-center justify-center text-[#6B7A99] hover:bg-[#0F1A2E] hover:text-white transition-all"
                aria-label="Suivant"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>

        {/* FEATURE SPOTLIGHT — NÉGOCIATION IA (Hero feature) */}
        <section className="relative py-32 px-6 overflow-hidden" aria-labelledby="ai-title">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0F1A2E] via-[#1B4FD8]/90 to-[#0F1A2E]"></div>
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#00D4AA]/20 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#FF4D6A]/10 rounded-full blur-[120px]"></div>

          <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
            <div className="text-white">
              <h2 id="ai-title" className="text-4xl lg:text-6xl font-black tracking-tight mb-6">
                Négociez vos contrats<br />
                <span className="bg-gradient-to-r from-[#00D4AA] to-white bg-clip-text text-transparent">sans rédiger un mot.</span>
              </h2>
              <p className="text-lg text-white/70 leading-relaxed mb-8">
                La Négociation IA génère des emails de résiliation et renégociation juridiquement solides, adaptés à chaque contrat. Un clic suffit pour transformer une alerte en action concrète.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: Brain, title: 'Analyse contextuelle', desc: 'L\'IA lit votre contrat, identifie les leviers juridiques et rédige un argumentaire sur mesure.' },
                  { icon: Target, title: 'Quatre intentions', desc: 'Résiliation, renégociation tarifaire, contestation de reconduction, ou demande de préavis.' },
                  { icon: Send, title: 'Prêt à envoyer', desc: 'Email copié dans votre presse-papier, signature automatique, ton professionnel préservé.' },
                ].map((feat, i) => {
                  const Icon = feat.icon;
                  return (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#00D4AA]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#00D4AA]" aria-hidden="true" />
                      </div>
                      <div>
                        <div className="font-bold mb-0.5">{feat.title}</div>
                        <div className="text-sm text-white/60">{feat.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <a href="/signup" className="inline-flex items-center gap-2 bg-[#00D4AA] text-[#0F1A2E] font-black px-6 py-3.5 rounded-xl hover:shadow-xl hover:shadow-[#00D4AA]/40 transition-all">
                Essayer la Négociation IA gratuitement →
              </a>
            </div>

            <div className="relative">
              <div className="bg-white rounded-3xl p-6 shadow-2xl shadow-black/30 relative overflow-hidden">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-[#1B4FD8] to-[#00D4AA] rounded-lg flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="text-xs font-black">Négociation IA</div>
                      <div className="text-[10px] text-[#6B7A99]">Orange Business · 340€/mois</div>
                    </div>
                  </div>
                  <div className="text-[10px] bg-[#00D4AA]/15 text-[#00D4AA] font-bold px-2 py-1 rounded-full">Généré</div>
                </div>

                <div className="bg-[#F7F9FC] rounded-xl p-4 mb-4">
                  <div className="text-[10px] text-[#6B7A99] font-bold uppercase tracking-wider mb-2">Objet</div>
                  <div className="text-sm font-bold mb-4 text-[#0F1A2E]">Résiliation du contrat n°ORG-2024-0847</div>

                  <div className="text-[10px] text-[#6B7A99] font-bold uppercase tracking-wider mb-2">Corps du message</div>
                  <div className="text-xs text-[#0F1A2E]/80 leading-relaxed space-y-2">
                    <p>Madame, Monsieur,</p>
                    <p>Conformément à l'article L.121-21 du Code de la consommation et aux conditions générales de notre contrat signé le 15/03/2024, je vous notifie par la présente ma volonté de ne pas reconduire notre contrat qui arrive à échéance le <span className="bg-[#00D4AA]/20 px-1 rounded">15/04/2026</span>.</p>
                    <p>Le préavis de <span className="bg-[#1B4FD8]/10 px-1 rounded">deux mois</span> étant respecté, je vous prie de procéder à la résiliation effective à cette date.</p>
                    <p className="text-[#6B7A99]">[...]</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button className="flex-1 bg-gradient-to-br from-[#0F1A2E] to-[#1B4FD8] text-white text-xs font-black py-2.5 rounded-lg">
                    Copier l'email
                  </button>
                  <button className="bg-[#F7F9FC] text-[#0F1A2E] text-xs font-bold py-2.5 px-4 rounded-lg">
                    Modifier
                  </button>
                </div>
              </div>

              <div className="absolute -top-6 -right-6 bg-white rounded-2xl p-3 shadow-xl shadow-black/20 border border-black/[0.08]" style={{ animation: 'float 4s ease-in-out infinite' }}>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#00D4AA] animate-pulse"></div>
                  <span className="text-[10px] font-bold">Généré en 3,2 s</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURES GRID */}
        <section className="py-32 px-6" aria-labelledby="features-title">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block text-xs font-black text-[#00D4AA] uppercase tracking-[0.3em] mb-4">Fonctionnalités</div>
              <h2 id="features-title" className="text-4xl lg:text-6xl font-black tracking-tight">
                Pensé pour les<br />
                <span className="bg-gradient-to-r from-[#1B4FD8] to-[#00D4AA] bg-clip-text text-transparent">PME françaises.</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: Sparkles, title: 'Extraction IA automatique', desc: 'Glissez un PDF. L\'intelligence artificielle identifie et extrait automatiquement les dates, les montants, les clauses de reconduction et les préavis. Vous n\'avez plus à saisir manuellement.', color: '#00D4AA' },
                { icon: Bell, title: 'Alertes automatiques', desc: 'Rappels à J-90, J-30 et J-7 avant chaque échéance critique. Configurables par contrat, par utilisateur. Zéro oubli, zéro reconduction non souhaitée.', color: '#FF4D6A' },
                { icon: BarChart3, title: 'Dashboard financier', desc: 'Visualisez en temps réel votre engagement mensuel et annuel, vos économies, vos prochaines échéances. Exportez votre rapport détaillé en un clic pour votre comptable.', color: '#1B4FD8' },
                { icon: MessageSquare, title: 'Négociation IA', desc: 'Générez en un clic un email de résiliation, renégociation ou contestation. Juridiquement solide, personnalisé à votre contrat, prêt à envoyer. Un gain de temps considérable.', color: '#00D4AA' },
                { icon: Users, title: 'Gestion d\'équipe', desc: 'Invitez les membres de votre équipe avec des rôles précis : administrateur pour gérer tout, éditeur pour modifier les contrats, lecteur pour consulter. Le nombre de membres inclus dépend de votre plan.', color: '#1B4FD8' },
                { icon: FileDown, title: 'Rapports exportables', desc: 'Générez des rapports PDF mensuels et trimestriels prêts à partager en réunion ou à transmettre à votre expert-comptable. Données propres, design professionnel.', color: '#FFB347' },
                { icon: Brain, title: 'Conseiller IA', desc: 'Votre assistant analyse vos contrats en temps réel et détecte automatiquement les économies potentielles. Posez-lui n\'importe quelle question sur vos engagements.', color: '#1B4FD8', badge: 'from-[#1B4FD8] to-[#00D4AA]' },
                { icon: Building2, title: 'Multi-Société', desc: 'Gérez les contrats de plusieurs entités juridiques depuis un seul compte. Switchez entre vos sociétés en un clic, chaque portefeuille reste isolé.', color: '#8B5CF6', badge: 'from-[#8B5CF6] to-[#1B4FD8]' },
              ].map((f, i) => (
                <article key={i} className="group relative bg-white border border-black/[0.06] rounded-3xl p-6 hover:border-transparent hover:shadow-2xl hover:shadow-[#0F1A2E]/10 transition-all hover:-translate-y-1 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `linear-gradient(135deg, ${f.color}08, transparent)` }}></div>
                  <div className="relative">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 transition-all group-hover:scale-110" style={{ background: `${f.color}15`, color: f.color }}>
                      <f.icon className="w-6 h-6" aria-hidden="true" />
                    </div>
                    {f.badge && (
                      <span className={`inline-block bg-gradient-to-r ${f.badge} text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide mb-2`}>
                        NOUVEAU
                      </span>
                    )}
                    <h3 className="font-black text-lg mb-2 tracking-tight">{f.title}</h3>
                    <p className="text-sm text-[#6B7A99] leading-relaxed">{f.desc}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* BLOG */}
        <section id="blog" className="py-24 px-6 bg-[#F7F9FC]" aria-labelledby="blog-title">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <div className="inline-flex items-center gap-2 text-xs font-black text-[#1B4FD8] uppercase tracking-[0.3em] mb-3">
                  <BookOpen className="w-3.5 h-3.5" /> Ressources
                </div>
                <h2 id="blog-title" className="text-3xl md:text-5xl font-black tracking-tight text-[#0F1A2E] leading-tight">
                  Ce que vous devriez<br />
                  <span className="bg-gradient-to-r from-[#1B4FD8] to-[#00D4AA] bg-clip-text text-transparent">savoir sur vos contrats</span>
                </h2>
              </div>
              <Link
                to="/blog"
                className="flex items-center gap-2 text-sm font-semibold text-[#1B4FD8] hover:text-[#0F1A2E] transition-colors group"
              >
                Tous les articles
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {articles.map((article, i) => (
                <Link
                  key={article.slug}
                  to={`/blog/${article.slug}`}
                  data-testid={`landing-blog-${article.slug}`}
                  className="group bg-white border border-black/[0.08] rounded-3xl p-6 hover:-translate-y-1 transition-all duration-300 flex flex-col"
                >
                  <div className={`w-full h-28 rounded-2xl flex items-center justify-center mb-5 text-4xl ${
                    i === 0 ? 'bg-gradient-to-br from-[#00D4AA]/10 to-[#1B4FD8]/10' :
                    i === 1 ? 'bg-gradient-to-br from-[#1B4FD8]/10 to-[#8B5CF6]/10' :
                    'bg-gradient-to-br from-[#FFB347]/10 to-[#FF4D6A]/10'
                  }`}>
                    {i === 0 ? '🧠' : i === 1 ? '🔍' : '⚖️'}
                  </div>
                  <span className="text-[10px] font-black text-[#00D4AA] uppercase tracking-[0.25em] mb-2 block">{article.category}</span>
                  <h3 className="font-black text-[#0F1A2E] leading-snug mb-3 group-hover:text-[#1B4FD8] transition-colors text-sm md:text-base flex-1">
                    {article.title}
                  </h3>
                  <div className="flex items-center justify-between pt-4 border-t border-black/[0.06] mt-2">
                    <span className="flex items-center gap-1 text-xs text-[#6B7A99]">
                      <Clock className="w-3 h-3" /> {article.readingTime}
                    </span>
                    <span className="flex items-center gap-1 text-xs font-semibold text-[#0F1A2E] group-hover:text-[#1B4FD8] transition-colors">
                      Lire <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section id="tarifs" className="py-32 px-6" aria-labelledby="pricing-title">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block text-xs font-black text-[#00D4AA] uppercase tracking-[0.3em] mb-4">Tarifs</div>
              <h2 id="pricing-title" className="text-4xl lg:text-6xl font-black tracking-tight mb-4">
                Tarifs simples.<br />
                <span className="bg-gradient-to-r from-[#1B4FD8] to-[#00D4AA] bg-clip-text text-transparent">Transparents.</span>
              </h2>
              <p className="text-lg text-[#6B7A99]">Sans engagement. Sans surprise.</p>
            </div>

            <div className="flex justify-center mb-12">
              <div className="inline-flex bg-[#F7F9FC] rounded-full p-1 border border-black/[0.04]" role="tablist">
                {[
                  { id: 'mensuel', label: 'Mensuel' },
                  { id: 'trimestriel', label: 'Trimestriel', badge: '-5%', pop: true },
                  { id: 'annuel', label: 'Annuel', badge: '-10%' },
                ].map(p => (
                  <button key={p.id} onClick={() => setPeriod(p.id)} type="button" role="tab" aria-selected={period === p.id}
                    className={`relative px-5 py-2.5 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${period === p.id ? 'bg-white shadow-md text-[#0F1A2E]' : 'text-[#6B7A99]'}`}>
                    {p.label}
                    {p.badge && (
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${p.id === 'trimestriel' ? 'bg-[#00D4AA] text-white' : 'bg-[#1B4FD8] text-white'}`}>
                        {p.badge}
                      </span>
                    )}
                    {p.pop && period !== p.id && (
                      <span className="absolute -top-2 -right-1 text-[8px] font-black px-2 py-0.5 rounded-full bg-[#FF4D6A] text-white shadow-lg shadow-[#FF4D6A]/30 animate-pulse">★</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans.map(plan => {
                const price = getPrice(plan.monthly);
                return (
                  <article key={plan.id} className={`relative bg-white rounded-3xl p-6 transition-all hover:-translate-y-1 ${plan.popular ? 'border-2 border-[#1B4FD8] shadow-2xl shadow-[#1B4FD8]/15' : 'border border-black/[0.08] hover:shadow-xl hover:shadow-[#0F1A2E]/5'}`}>
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#1B4FD8] to-[#00D4AA] text-white text-[10px] font-black px-4 py-1 rounded-full tracking-wider shadow-lg shadow-[#1B4FD8]/30">
                        ★ POPULAIRE
                      </div>
                    )}
                    <h3 className="font-black text-sm mb-3 tracking-tight">{plan.name}</h3>
                    <div className="mb-1">
                      <span className="text-3xl font-black tracking-tighter tabular-nums">{price.display}</span>
                      <span className="text-xs text-[#6B7A99] ml-1">{price.period}</span>
                    </div>
                    {price.equiv && <div className="text-[10px] mb-4 font-medium" style={{ color: periodColor }}>{price.equiv}</div>}
                    {!price.equiv && <div className="mb-4 h-[14px]"></div>}
                    <ul className="space-y-2 mb-6">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs">
                          <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${periodColor}25` }}>
                            <Check className="w-2.5 h-2.5" style={{ color: periodColor }} aria-hidden="true" />
                          </div>
                          <span className="text-[#0F1A2E]/80 font-medium">{f}</span>
                        </li>
                      ))}
                    </ul>
                    {plan.monthly === 0 ? (
                      <a href="/signup" className="block text-center py-3 rounded-xl text-sm font-black transition-all border-2 border-black/[0.08] hover:border-[#0F1A2E] hover:bg-[#0F1A2E] hover:text-white">
                        Commencer
                      </a>
                    ) : (
                      <button
                        onClick={() => handlePlanSelect(plan.id)}
                        className={`w-full py-3 rounded-xl text-sm font-black transition-all ${plan.popular ? 'bg-gradient-to-br from-[#0F1A2E] to-[#1B4FD8] text-white hover:shadow-lg hover:shadow-[#1B4FD8]/30' : 'border-2 border-black/[0.08] hover:border-[#0F1A2E] hover:bg-[#0F1A2E] hover:text-white'}`}
                      >
                        Choisir
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-32 px-6 max-w-3xl mx-auto" aria-labelledby="faq-title">
          <div className="text-center mb-16">
            <div className="inline-block text-xs font-black text-[#1B4FD8] uppercase tracking-[0.3em] mb-4">FAQ</div>
            <h2 id="faq-title" className="text-4xl lg:text-6xl font-black tracking-tight">
              Questions<br />
              <span className="bg-gradient-to-r from-[#1B4FD8] to-[#00D4AA] bg-clip-text text-transparent">fréquentes.</span>
            </h2>
          </div>

          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <div key={i} className={`border rounded-2xl overflow-hidden bg-white transition-all ${openFaq === i ? 'border-[#1B4FD8]/30 shadow-lg shadow-[#1B4FD8]/5' : 'border-black/[0.06]'}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} type="button" aria-expanded={openFaq === i} className="w-full flex items-center justify-between p-6 text-left hover:bg-[#F7F9FC]/50 transition-colors">
                  <span className="font-bold text-[#0F1A2E]">{faq.q}</span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all flex-shrink-0 ml-4 ${openFaq === i ? 'bg-[#1B4FD8] rotate-180' : 'bg-[#F7F9FC]'}`}>
                    <ChevronDown className={`w-4 h-4 transition-colors ${openFaq === i ? 'text-white' : 'text-[#6B7A99]'}`} aria-hidden="true" />
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-[600px]' : 'max-h-0'}`}>
                  <div className="px-6 pb-6 text-sm text-[#6B7A99] leading-relaxed">{faq.a}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CONTACT */}
        <section id="contact" className="py-32 px-6 bg-[#F7F9FC]" aria-labelledby="contact-title">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block text-xs font-black text-[#1B4FD8] uppercase tracking-[0.3em] mb-4">Contact</div>
            <h2 id="contact-title" className="text-4xl lg:text-6xl font-black tracking-tight mb-4">
              Une question ?{' '}
              <span className="bg-gradient-to-r from-[#1B4FD8] to-[#00D4AA] bg-clip-text text-transparent">Parlons-en.</span>
            </h2>
            <p className="text-lg text-[#6B7A99] mb-12">Notre équipe répond sous 24h, du lundi au samedi de 8h30 à 19h.</p>

            <div className="max-w-2xl mx-auto mb-8">
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="bg-white border border-black/[0.06] rounded-2xl p-8 text-center hover:shadow-xl transition-all">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#1B4FD8]/20 to-[#1B4FD8]/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                    <Mail className="w-7 h-7 text-[#1B4FD8]" aria-hidden="true" />
                  </div>
                  <div className="text-xs font-black text-[#6B7A99] uppercase tracking-[0.2em] mb-2">Par email</div>
                  <a href="mailto:hello@mycontrat.fr" className="text-xl font-black tracking-tight text-[#1B4FD8] hover:underline block mb-1">hello@mycontrat.fr</a>
                  <div className="text-sm text-[#6B7A99]">Réponse sous 24h</div>
                </div>
                <div className="bg-white border border-black/[0.06] rounded-2xl p-8 text-center hover:shadow-xl transition-all">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#00D4AA]/20 to-[#00D4AA]/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                    <Calendar className="w-7 h-7 text-[#00D4AA]" aria-hidden="true" />
                  </div>
                  <div className="text-xs font-black text-[#6B7A99] uppercase tracking-[0.2em] mb-2">Démo produit</div>
                  <div className="text-xl font-black tracking-tight mb-1">30 min, en visio</div>
                  <div className="text-sm text-[#6B7A99]">Lun – Ven, 9h – 18h</div>
                </div>
              </div>
            </div>

            <a href="mailto:hello@mycontrat.fr?subject=Demande%20de%20d%C3%A9mo%20MyContrat" className="inline-block bg-[#00D4AA] text-[#0F1A2E] font-bold px-6 py-3 rounded-xl hover:shadow-lg transition-all">
              Planifier une démo →
            </a>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-24 px-6">
          <div className="relative max-w-6xl mx-auto bg-gradient-to-br from-[#0F1A2E] via-[#1B4FD8] to-[#0F1A2E] text-white rounded-[40px] p-12 lg:p-20 text-center overflow-hidden">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#00D4AA]/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#FF4D6A]/10 rounded-full blur-3xl"></div>

            <div className="relative">
              <h2 className="text-4xl lg:text-7xl font-black tracking-tighter mb-6">
                Commencez à économiser<br />
                <span className="bg-gradient-to-r from-[#00D4AA] to-white bg-clip-text text-transparent">dès aujourd'hui.</span>
              </h2>
              <p className="text-lg text-white/70 mb-10 max-w-xl mx-auto">Essai gratuit. 3 contrats offerts. Aucune carte bancaire.</p>
              <a href="/signup" className="inline-flex items-center gap-3 bg-[#00D4AA] text-[#0F1A2E] font-black text-lg px-10 py-5 rounded-2xl hover:shadow-2xl hover:shadow-[#00D4AA]/40 transition-all hover:scale-105 group">
                Créer mon compte gratuit
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>

        <footer className="border-t border-black/[0.06] py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-5 gap-8 mb-12">
              <div className="md:col-span-2">
                <div className="mb-3">
                  <Logo size={36} showText={true} linkTo="/" />
                </div>
                <p className="text-sm text-[#6B7A99] max-w-xs">Le logiciel de gestion de contrats pensé pour les PME françaises. Extraction IA, alertes automatiques, négociation intégrée.</p>
              </div>
              <div>
                <div className="text-xs font-black uppercase tracking-wider mb-3">Produit</div>
                <ul className="space-y-2 text-sm text-[#6B7A99]">
                  <li><a href="#" className="hover:text-[#0F1A2E]">Fonctionnalités</a></li>
                  <li><a href="#" className="hover:text-[#0F1A2E]">Tarifs</a></li>
                  <li><a href="#" className="hover:text-[#0F1A2E]">Calculateur d'économies</a></li>
                </ul>
              </div>
              <div>
                <div className="text-xs font-black uppercase tracking-wider mb-3">Ressources</div>
                <ul className="space-y-2 text-sm text-[#6B7A99]">
                  <li><Link to="/blog" className="hover:text-[#0F1A2E]">Blog</Link></li>
                  <li><a href="#" className="hover:text-[#0F1A2E]">Contact</a></li>
                  <li><a href="#" className="hover:text-[#0F1A2E]">Support</a></li>
                </ul>
              </div>
              <div>
                <div className="text-xs font-black uppercase tracking-wider mb-3">Légal</div>
                <ul className="space-y-2 text-sm text-[#6B7A99]">
                  <li><Link to="/cgv" className="hover:text-[#0F1A2E]">CGV</Link></li>
                  <li><Link to="/rgpd" className="hover:text-[#0F1A2E]">Confidentialité & RGPD</Link></li>
                  <li><Link to="/mentions-legales" className="hover:text-[#0F1A2E]">Mentions légales</Link></li>
                </ul>
              </div>
            </div>
            <div className="pt-8 border-t border-black/[0.06] text-xs text-[#6B7A99] flex justify-between">
              <div>© 2026 MyContrat — Tous droits réservés</div>
              <div>MyContrat V.1.12.343</div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}